import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { colors, shell, type } from '../theme';
import { BrandMark } from './BrandMark';
import { Wordmark } from './Wordmark';

/**
 * The desktop shell: a fixed sidebar and a padded main column.
 *
 * Every one of the 16 screen artboards on page-2 of the canvas draws the same
 * frame, to the pixel: a 236px sidebar with 24px/16px padding, nav rows at
 * 11px/14px with a 12px radius and a 4px gap, a footer identity block above a
 * one-pixel rgba rule, and a main column at 32px/36px with a 24px stack gap.
 * Those numbers are in `theme/tokens.ts` under `shell`, not here.
 *
 * `surface` is the one thing the two products do not share. The manufacturer
 * portal's sidebar is navy and the staff console's is the darker scanner
 * surface, and that difference is the only way a screenshot says which system
 * it came from. It is drawn, so it is not a preference.
 *
 * With `onSignOut`, the identity block opens a menu holding one item, Sign out:
 * the user's ruling on X5. No board draws the menu, so its look is D28.
 */

export type NavItem = {
  key: string;
  label: string;
  icon: ReactNode;
  /** The orange count badge. Omitted and zero both render nothing. */
  badge?: number;
};

export type ShellProps = {
  surface: 'portal' | 'console';
  /** The 10px eyebrow under the wordmark: "MANUFACTURER PORTAL", "STAFF CONSOLE". */
  eyebrow: string;
  nav: NavItem[];
  activeKey: string;
  onNavigate?: (key: string) => void;
  /** The sidebar footer: who is signed in, and to what. */
  identity: { initial: string; name: string; detail: string };
  /** Given, the identity block opens a menu holding Sign out. */
  onSignOut?: () => void;
  children: ReactNode;
};

const sidebar = (surface: ShellProps['surface']): CSSProperties => ({
  width: shell.sidebarWidth,
  background: shell.ground[surface],
  padding: `${shell.sidebarPadding.y}px ${shell.sidebarPadding.x}px`,
  display: 'flex',
  flexDirection: 'column',
  gap: shell.sidebarPadding.y,
  flexShrink: 0,
});

const navRow = (active: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: shell.navRow.iconGap,
  padding: `${shell.navRow.paddingY}px ${shell.navRow.paddingX}px`,
  borderRadius: shell.navRow.radius,
  background: active ? colors.brand : 'transparent',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  font: 'inherit',
});

/** On the active row the badge inverts, white on brand text, as `PortalReportsInbox` draws it. */
const badgeStyle = (active: boolean): CSSProperties => ({
  minWidth: 20,
  height: 20,
  borderRadius: 10,
  background: active ? colors.white : colors.brand,
  color: active ? colors.brand : colors.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...type.badge,
});

export function Shell({
  surface,
  eyebrow,
  nav,
  activeKey,
  onNavigate,
  identity,
  onSignOut,
  children,
}: ShellProps) {
  return (
    <div style={{ minHeight: '100vh', background: colors.ground, display: 'flex' }}>
      <nav style={sidebar(surface)} aria-label="Primary">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 6px' }}>
          <BrandMark decorative />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Wordmark size={19} color={colors.white} />
            <div
              style={{
                ...type.eyebrow,
                color: surface === 'console' ? colors.brand : colors.mutedOnNavy,
              }}
            >
              {eyebrow}
            </div>
          </div>
        </div>

        <ul
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: shell.navRow.gap,
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {nav.map((item) => {
            const active = item.key === activeKey;
            return (
              <li key={item.key}>
                <button
                  className="vp-nav"
                  type="button"
                  style={navRow(active)}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => onNavigate?.(item.key)}
                >
                  {item.icon}
                  <span
                    style={{
                      flexGrow: 1,
                      ...(active ? type.bodyBold : type.bodyStrong),
                      color: active ? colors.white : colors.mutedOnNavy,
                    }}
                  >
                    {item.label}
                  </span>
                  {item.badge ? (
                    <span style={badgeStyle(active)} aria-label={`${item.badge} waiting`}>
                      {item.badge}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>

        <div style={{ flexGrow: 1 }} />

        <div
          style={{
            borderTop: `1px solid ${shell.sidebarDivider}`,
            padding: '16px 0 0 0',
            position: 'relative',
          }}
        >
          {onSignOut ? (
            <IdentityMenu identity={identity} onSignOut={onSignOut} />
          ) : (
            <div style={identityRow}>
              <IdentityFace identity={identity} />
            </div>
          )}
        </div>
      </nav>

      <main
        style={{
          flexGrow: 1,
          padding: `${shell.mainPadding.y}px ${shell.mainPadding.x}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: shell.mainGap,
          minWidth: 0,
        }}
      >
        {children}
      </main>
    </div>
  );
}

/** The drawn footer: the 36px brand disc with the initial, then two lines. */
function IdentityFace({ identity }: { identity: ShellProps['identity'] }) {
  return (
    <>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 18,
          background: colors.brand,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ...type.cardTitle,
          color: colors.white,
        }}
        aria-hidden
      >
        {identity.initial}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <div style={{ ...type.metaBold, color: colors.white }}>{identity.name}</div>
        <div style={{ ...type.captionSm, color: colors.mutedOnNavy }}>{identity.detail}</div>
      </div>
    </>
  );
}

/**
 * The identity block as a menu button, the WAI-ARIA menu button pattern. Enter,
 * Space and the down arrow open it on its item; Escape closes it and returns
 * the focus; Tab or a click anywhere else closes it.
 */
function IdentityMenu({
  identity,
  onSignOut,
}: {
  identity: ShellProps['identity'];
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const item = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    item.current?.focus();
    const away = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  function close() {
    setOpen(false);
    button.current?.focus();
  }

  function onMenuKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'Tab') {
      setOpen(false);
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      // One item: every move lands on it.
      event.preventDefault();
      item.current?.focus();
    }
  }

  return (
    <div ref={wrapper}>
      {open ? (
        <div role="menu" aria-label="Account" onKeyDown={onMenuKey} style={menuBox}>
          <button
            className="vp-outline"
            ref={item}
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            style={menuItem}
          >
            Sign out
          </button>
        </div>
      ) : null}
      <button
        className="vp-nav"
        ref={button}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${identity.name}, ${identity.detail}`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
          }
        }}
        style={{
          ...identityRow,
          width: '100%',
          background: 'transparent',
          border: 'none',
          borderRadius: shell.navRow.radius,
          textAlign: 'left',
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        <IdentityFace identity={identity} />
      </button>
    </div>
  );
}

/** The drawn footer row: `16px 6px 0` around the disc and lines, the top padding on the rule. */
const identityRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '0 6px',
};

/** Not drawn, D28: the portal's own card and nav-row values, above the block it opens from. */
const menuBox: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: '100%',
  marginBottom: 8,
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: shell.navRow.radius,
  padding: 4,
};

const menuItem: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: `${shell.navRow.paddingY}px ${shell.navRow.paddingX}px`,
  borderRadius: shell.navRow.radius - 4,
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  cursor: 'pointer',
  ...type.bodyStrong,
  color: colors.navy,
};

/** The page header every main column opens with: title, subtitle, one action. */
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: shell.mainGap,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h1 style={{ ...type.pageTitle, color: colors.navy, margin: 0 }}>{title}</h1>
        {subtitle ? (
          <p style={{ ...type.body, color: colors.mutedInk, margin: 0 }}>{subtitle}</p>
        ) : null}
      </div>
      {action}
    </header>
  );
}
